import { useState, useMemo, useRef, useEffect } from "react";
import { motion } from "@/motion/appMotion";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { pricingContent as defaultPricingContent } from "../../../data/landingPage/pricingContent";
import usePublicPlans from "../../../hooks/usePublicPlans";
import { buildPlanAnnualSavingsLabel, planHasAnnualSavings } from "../../../utils/publicPlans";
import useLandingMotion from "../../../hooks/useLandingMotion";
import { useSiteConfig } from "../../../context/SiteConfigContext";
import { buildPricingDescriptionText } from "../../../utils/trialPromo";
import faqIllustration from "../../../assets/image/faq.svg";
import pricingBgShade from "../../../assets/image/pricing_bg.svg";
import {
  LANDING_CAROUSEL_BLEED_X,
  LANDING_CONTAINER_PADDED,
  LANDING_INNER,
} from "../../../constants/landingPageLayout";

const GRADIENT_TEXT_STYLE = {
  backgroundImage: "linear-gradient(180deg, #7D3BEE 0%, #4F46E5 100%)",
  WebkitBackgroundClip: "text",
  backgroundClip: "text",
  color: "transparent",
  WebkitTextFillColor: "transparent",
};

const PricingHeroHeading = ({
  headingBefore,
  headingAccent,
  description,
}) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.55 }}
    className="relative z-10 mx-auto flex w-full max-w-[852px] flex-col items-center gap-3 text-center sm:gap-[12px]"
  >
    <h2
      className="max-w-[852px] text-[clamp(32px,6vw,68px)] font-bold leading-[1.06] text-[#0F172A] sm:leading-[72px]"
      style={{ fontFamily: '"Rethink Sans", sans-serif' }}
    >
      {headingBefore}
      <span style={GRADIENT_TEXT_STYLE}>{headingAccent}</span>
    </h2>

    <p
      className="max-w-[634px] text-[16px] font-normal leading-[1.4] text-[#64748B] sm:text-[18px] sm:leading-none"
      style={{ fontFamily: '"Inter", sans-serif' }}
    >
      {description}
    </p>
  </motion.div>
);

const PricingBackground = () => (
  <div
    className="pointer-events-none absolute inset-x-0 top-0 z-0 h-[720px] sm:h-[820px] lg:h-[900px]"
    aria-hidden
  >
    <img
      src={pricingBgShade}
      alt=""
      className="absolute inset-x-0 top-[140px] h-auto w-full sm:top-[220px] lg:top-[290px]"
    />
    <div className={`absolute inset-x-0 top-0 mx-auto h-full ${LANDING_CONTAINER_PADDED}`}>
      <img
        src={faqIllustration}
        alt=""
        className="absolute left-1/2 top-[50px] h-auto w-[min(781px,90vw)] max-w-[781px] -translate-x-1/2 sm:top-[70px] lg:left-[250.36px] lg:top-[90.2px] lg:h-[442.99px] lg:w-[781.44px] lg:max-w-none lg:translate-x-0"
        width={781}
        height={443}
      />
    </div>
  </div>
);

const Pricing = ({
  content,
  detailLinkHref,
  showHeroBackground = false,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { config } = useSiteConfig();
  const pricingContent = content || defaultPricingContent;
  const [isAnnual, setIsAnnual] = useState(true);
  const { plans, annualToggleSavingsLabel } = usePublicPlans();
  const { getInViewVariants } = useLandingMotion();

  const cardsScrollRef = useRef(null);
  const popularCardRef = useRef(null);

  useEffect(() => {
    const container = cardsScrollRef.current;
    if (!container) return;

    const centerPopularCard = () => {
      if (window.matchMedia("(min-width: 1024px)").matches) return;

      const target =
        popularCardRef.current ||
        container.querySelectorAll("[data-pricing-card]")[
          Math.floor(plans.length / 2)
        ];
      if (!target) return;

      const left =
        target.offsetLeft - (container.clientWidth - target.offsetWidth) / 2;
      container.scrollTo({ left: Math.max(0, left), behavior: "auto" });
    };

    const id = requestAnimationFrame(centerPopularCard);
    window.addEventListener("resize", centerPopularCard);

    return () => {
      cancelAnimationFrame(id);
      window.removeEventListener("resize", centerPopularCard);
    };
  }, [plans]);

  const savingsLabel = annualToggleSavingsLabel;

  const description = useMemo(() => {
    if (pricingContent.description && !pricingContent.descriptionIncludesTrial) {
      return pricingContent.description;
    }
    return buildPricingDescriptionText(config?.trial_days);
  }, [pricingContent.description, pricingContent.descriptionIncludesTrial, config?.trial_days]);

  const resolvedDetailHref =
    detailLinkHref ??
    pricingContent.additionalInfo?.linkHref ??
    "/pricing";

  const handleViewPricingDetails = () => {
    if (resolvedDetailHref.startsWith("#")) {
      const id = resolvedDetailHref.slice(1);
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
        window.history.replaceState(
          null,
          "",
          `${location.pathname}${location.search}${resolvedDetailHref}`,
        );
      }
      return;
    }
    navigate(resolvedDetailHref);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const calculatePrice = (plan) => {
    return isAnnual ? plan.annualPrice : plan.monthlyPrice;
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
  };

  return (
    <section id="pricing" className="relative w-full overflow-hidden bg-white font-sans">
      {showHeroBackground && <PricingBackground />}

      <div
        className={`relative z-10 ${LANDING_CONTAINER_PADDED} pb-16 lg:pb-24 ${
          showHeroBackground ? "pt-28 sm:pt-32 lg:pt-36" : "pt-12 sm:pt-16 lg:pt-20"
        }`}
      >
        <div className={LANDING_INNER}>
        <PricingHeroHeading
          headingBefore={pricingContent.headingBefore}
          headingAccent={pricingContent.headingAccent}
          description={description}
        />

        {/* Toggle */}
        <div className="relative z-10 mb-8 mt-8 flex justify-center sm:mb-10 sm:mt-10 lg:mb-12">
          <div className="inline-flex max-w-full flex-wrap items-center justify-center rounded-full border border-slate-100 bg-slate-50 p-1 shadow-[inset_0_2px_4px_rgba(0,0,0,0.02)]">
            <button
              onClick={() => setIsAnnual(false)}
              className={`rounded-full px-4 py-2 text-[13px] transition-all duration-200 sm:px-7 sm:py-2.5 sm:text-[14px] ${
                !isAnnual
                  ? "bg-white font-medium text-slate-900 shadow-[0_1px_3px_rgba(0,0,0,0.1)]"
                  : "text-slate-500 hover:text-slate-700"
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setIsAnnual(true)}
              className={`flex items-center gap-1.5 rounded-full px-4 py-2 text-[13px] transition-all duration-200 sm:gap-2 sm:px-7 sm:py-2.5 sm:text-[14px] ${
                isAnnual
                  ? "bg-white font-medium text-indigo-600 shadow-[0_1px_3px_rgba(0,0,0,0.1)]"
                  : "text-slate-500 hover:text-slate-700"
              }`}
            >
              Annual
              {annualToggleSavingsLabel !== 'Annual billing' && (
                <span className="rounded-full bg-[#E6F6ED] px-2 py-0.5 text-[9px] font-bold tracking-wide text-[#00A95D] sm:px-2.5 sm:text-[10px]">
                  {savingsLabel}
                </span>
              )}
            </button>
          </div>
        </div>

        {/* Pricing Cards — layered on top of hero background */}
        <motion.div
          ref={cardsScrollRef}
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          className={`relative z-10 flex max-w-none snap-x snap-mandatory items-stretch gap-4 overflow-x-auto overscroll-x-contain pt-8 pb-4 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden sm:gap-5 md:gap-6 lg:mx-auto lg:max-w-6xl lg:flex-row lg:items-end lg:justify-center lg:gap-6 lg:overflow-visible lg:pt-0 lg:pb-0 ${LANDING_CAROUSEL_BLEED_X}`}
        >
          {/* Edge spacers — allow every card (incl. first/last) to snap-center with ~10% side peek */}
          <div
            aria-hidden
            className="shrink-0 w-[calc((100vw-min(366px,80vw))/2)] lg:hidden"
          />

          {plans.map((plan) => {
            const currentPrice = calculatePrice(plan);
            const isPopular = plan.popular;
            const showAnnualSavings = isAnnual && planHasAnnualSavings(plan);
            const planSavingsLabel = showAnnualSavings
              ? buildPlanAnnualSavingsLabel(plan)
              : null;
            const annualTotal =
              plan.annualTotal > 0
                ? plan.annualTotal
                : (Number(plan.annualPrice) || 0) * 12;

            const popularCardStyle = {
              border: "1px solid transparent",
              background:
                "linear-gradient(#fff, #fff) padding-box, linear-gradient(161.11deg, #7D3BEE -0.02%, #FFFFFF 50.25%, #4F46E5 97.11%) border-box",
            };

            return (
              <motion.div
                key={plan.name}
                ref={isPopular ? popularCardRef : undefined}
                data-pricing-card
                variants={itemVariants}
                style={isPopular ? popularCardStyle : undefined}
                className={`relative flex w-[min(366px,80vw)] shrink-0 snap-center snap-always flex-col rounded-[20px] bg-white px-5 pb-10 pt-9 transition-all duration-300 sm:px-6 sm:pb-10 sm:pt-10 lg:w-[366px] ${
                  isPopular
                    ? "z-20 shadow-lg lg:min-h-[760px] lg:px-[26px] lg:pb-[104px] lg:pt-[50px]"
                    : "z-20 border border-[#E2E8F0] shadow-md lg:min-h-[669px] lg:px-[26px] lg:pb-[34px] lg:pt-[35px]"
                }`}
              >
                {isPopular && (
                  <div className="absolute -top-3.5 left-0 right-0 flex justify-center sm:-top-4">
                    <span className="rounded-full bg-[#6366F1] px-4 py-1 text-[12px] font-medium tracking-wide text-white sm:px-6 sm:py-1.5 sm:text-[13px]">
                      Most Popular
                    </span>
                  </div>
                )}

                <div className="flex flex-col">
                  <div className="mb-4 mt-1 sm:mb-5 sm:mt-2">
                    <h3 className="mb-2 text-[20px] font-semibold text-slate-900 sm:text-[22px]">{plan.name}</h3>
                    <p className="min-h-10 pr-2 text-[13px] leading-relaxed text-slate-500 sm:pr-4 sm:text-[14px]">{plan.description}</p>
                  </div>

                  <div
                    className={`mb-5 flex flex-col gap-0.5 sm:mb-6 ${
                      isAnnual ? "min-h-[5.5rem] sm:min-h-[5.75rem]" : ""
                    }`}
                  >
                    {showAnnualSavings && plan.monthlyPrice > currentPrice && (
                      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                        <span className="text-[13px] font-medium text-slate-400 line-through sm:text-[14px]">
                          ₹{plan.monthlyPrice.toLocaleString("en-IN")}/mo
                        </span>
                        {planSavingsLabel && (
                          <span className="rounded-full bg-[#E6F6ED] px-2 py-0.5 text-[10px] font-bold tracking-wide text-[#00A95D] sm:text-[11px]">
                            {planSavingsLabel}
                          </span>
                        )}
                      </div>
                    )}
                    <div className="flex items-baseline">
                      <span className="text-[34px] font-bold leading-none tracking-tight text-slate-900 sm:text-[40px]">
                        ₹{currentPrice.toLocaleString("en-IN")}
                      </span>
                      <span className="ml-2 text-[14px] font-medium text-slate-600 sm:text-[15px]">/month</span>
                    </div>
                    {isAnnual && annualTotal > 0 && (
                      <p className={`text-[11px] leading-snug sm:text-[12px] ${showAnnualSavings ? "text-slate-500" : "text-transparent"}`}>
                        {showAnnualSavings
                          ? `Billed annually at ₹${annualTotal.toLocaleString("en-IN")}`
                          : "\u00A0"}
                      </p>
                    )}
                  </div>

                  <Link
                    to="/signup"
                    style={
                      isPopular
                        ? { background: "linear-gradient(180deg, #7D3BEE 0%, #4F46E5 100%)" }
                        : { background: "#0F172A" }
                    }
                    className="mb-6 w-full rounded-[12px] py-3 text-center text-[13px] font-semibold text-white shadow-sm transition-opacity duration-200 hover:opacity-90 sm:mb-8 sm:py-3.5 sm:text-[14px]"
                  >
                    Get Started
                  </Link>

                  <ul className="space-y-2.5 sm:space-y-3">
                    {plan.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start">
                        <svg
                          className="mr-2.5 h-[16px] w-[16px] shrink-0 text-[#818CF8] sm:mr-3 sm:h-[18px] sm:w-[18px]"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth="2.5"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                        <span className="pt-[1px] text-[13px] leading-tight text-slate-500 sm:text-[14px]">
                          {feature}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            );
          })}

          <div
            aria-hidden
            className="shrink-0 w-[calc((100vw-min(366px,80vw))/2)] lg:hidden"
          />
        </motion.div>

        {/* Footer */}
        <div className="mt-10 text-center sm:mt-12 lg:mt-16">
          <p className="mb-5 flex flex-wrap items-center justify-center gap-x-2.5 gap-y-1 px-2 text-[13px] text-slate-600 sm:mb-6 sm:px-0 sm:text-[14px]">
            <span>14-day free trial</span>
            <span className="text-[8px] text-slate-800">●</span>
            <span>No credit card required</span>
            <span className="text-[8px] text-slate-800">●</span>
            <span>Cancel anytime</span>
            <span className="text-[8px] text-slate-800">●</span>
            <Link to="/contact" className="font-medium text-[#6366F1] hover:text-indigo-700">
              Contact Sales
            </Link>
          </p>
          <button
            onClick={handleViewPricingDetails}
            className="group inline-flex items-center gap-0"
          >
            <span
              className="inline-flex h-[39.52px] items-center justify-center whitespace-nowrap rounded-[17.89px] bg-[#4F46E5] px-[10.44px] text-[13.42px] font-medium leading-none tracking-normal text-white shadow-md transition-colors duration-200 group-hover:bg-[#4338CA]"
              style={{ fontFamily: '"Inter", sans-serif' }}
            >
              View Full Pricing Details
            </span>
            <span className="inline-flex h-[39.52px] w-[39.52px] shrink-0 items-center justify-center rounded-full bg-[#4F46E5] p-[7.46px] text-white shadow-md transition-colors duration-200 group-hover:bg-[#4338CA]">
              <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 19.5l15-15m0 0H8.25m11.25 0v11.25" />
              </svg>
            </span>
          </button>
        </div>
        </div>
      </div>
    </section>
  );
};

export default Pricing;
